import { createContext, useContext, useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";

import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import ResendValidationButton from "Components/Admin/ResendValidationButton";

import * as S from "./styles";

// Contexto para expor as opções de status do vínculo e o callback de
// atualização às células da tabela. Necessário porque o componente Table
// memoiza as colunas na montagem (useMemo com deps vazias) — ler via contexto
// garante valores sempre atuais dentro das células.
export const LinkStatusContext = createContext({
  statusOptions: [],
  onSaved: () => {},
});

// Célula editável de "Status do Vínculo": select nativo que grava na API
// existente (POST /projects/update-project-sponsor) ao alterar o valor,
// exibindo um loading na própria linha enquanto o processo ocorre.
const LinkStatusSelect = ({
  sponsorId,
  statusId,
  fallbackLabel,
  validationEmailSentAt = null,
}) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { statusOptions, onSaved } = useContext(LinkStatusContext);

  const [value, setValue] = useState(statusId ?? "");
  const [saving, setSaving] = useState(false);

  // Sincroniza quando os dados da linha mudam (ex.: após salvar/recarregar).
  useEffect(() => {
    setValue(statusId ?? "");
  }, [statusId]);

  // Sem opções carregadas ou sem vínculo válido: mostra o texto atual.
  if (!statusOptions?.length || !sponsorId) {
    return <span>{fallbackLabel || "—"}</span>;
  }

  const handleChange = (event) => {
    const newStatusId = Number(event.target.value);

    if (!newStatusId || newStatusId === Number(value)) return;

    const previous = value;

    setValue(newStatusId);
    setSaving(true);

    api
      .post("/projects/update-project-sponsor", {
        sponsor_id: sponsorId,
        status_id: newStatusId,
      })
      .then(() => {
        showNotification(
          "success",
          "Sucesso!",
          "Status do vínculo atualizado!",
        );

        const option = statusOptions.find((o) => o.value === newStatusId);

        if (onSaved) onSaved(sponsorId, newStatusId, option?.label || null);
      })
      .catch((error) => {
        setValue(previous);
        return HTTPErrorCallback(error);
      })
      .finally(() => setSaving(false));
  };

  return (
    <S.Cell>
      <S.Select value={value} onChange={handleChange} disabled={saving}>
        {statusOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </S.Select>
      {saving && <S.Spinner aria-label="Salvando" />}
      {/* Só aparece em vínculo que já teve pedido enviado. */}
      <ResendValidationButton
        sponsorId={sponsorId}
        sentAt={validationEmailSentAt}
      />
    </S.Cell>
  );
};

export default LinkStatusSelect;
